import { useEffect } from 'react';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import type { ExportFlowStep, ExportResult } from '../types';
import { getQuotaFailureMessage } from '../lib/exportQuota';
import { getExportResultMessage } from '../lib/exportResultMessage';

interface ExportResultFeedbackOptions {
  result: ExportResult | null;
  quotaIsDemo: boolean;
  onAnnounce: (message: string) => void;
  onClear: () => void;
}

const FailedStepLabels = {
  license: '① license',
  calculate: '② local calculation',
  record: '③ run recording',
  write: '④ disk write',
} as const satisfies Record<ExportFlowStep, string>;

export function useExportResultFeedback({
  result,
  quotaIsDemo,
  onAnnounce,
  onClear,
}: ExportResultFeedbackOptions): void {
  const showSuccess = useSnackbarStore((state) => state.showSuccess);
  const showError = useSnackbarStore((state) => state.showError);
  const showWarning = useSnackbarStore((state) => state.showWarning);

  useEffect(() => {
    if (!result) {
      return;
    }

    const outcome = getExportResultMessage(result);
    let message: string;
    if (result.success || outcome.hasOutput) {
      const headerWarningSuffix = result.mappedHeaderWarning
        ? ` ${result.mappedHeaderWarning}`
        : '';
      message = `${outcome.message}${headerWarningSuffix}`;
      if (outcome.kind === 'success' && !result.mappedHeaderWarning) {
        showSuccess(message);
      } else if (outcome.kind === 'error') {
        showError(message);
      } else {
        showWarning(message);
      }
    } else if (result.quotaExhausted || result.quotaInsufficient) {
      message = getQuotaFailureMessage(
        result.isDemo ?? quotaIsDemo,
        result.remainingExportElements,
      );
      showError(message);
    } else {
      const stepLabel = result.failedStep ? FailedStepLabels[result.failedStep] : null;
      const stepPrefix = stepLabel ? `Failed at step ${stepLabel}. ` : '';
      message = `${stepPrefix}${outcome.message}`;
      showError(message);
    }

    onAnnounce(message);
    if ((result.groupFailures?.length ?? 0) === 0) {
      onClear();
    }
  }, [onAnnounce, onClear, quotaIsDemo, result, showError, showSuccess, showWarning]);
}
